import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { employeeService, departmentService } from '@/services/api'
import { UserPlus, Upload, Download, Search, Edit, Trash2, Layers, MonitorUp, FileSpreadsheet } from 'lucide-react'
import EmployeeModal from '@/components/employees/EmployeeModal'
import ImportModal from '@/components/common/ImportModal'
import BulkImportModal from '@/components/common/BulkImportModal'
import SyncToTerminalModal from '@/components/common/SyncToTerminalModal'
import Modal from '@/components/common/Modal'
import toast from 'react-hot-toast'
import clsx from 'clsx'

const STATUS_BADGE = {
  active: 'badge-green',
  on_leave: 'badge-yellow',
  fired: 'badge-red',
}

const STATUS_LABEL = {
  active: 'Активен',
  on_leave: 'В отпуске',
  fired: 'Уволен',
}

const SCHEDULE_LABEL = {
  five_day: '5-дневка',
  six_day: '6-дневка',
  shift: 'Сменный',
}

export default function EmployeesPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({ status: '', department: '', position: '' })
  const [page, setPage] = useState(1)
  const [modal, setModal] = useState({ open: false, data: null })
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [bulkImportOpen, setBulkImportOpen] = useState(false)
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null, name: '' })
  const [deleting, setDeleting] = useState(false)

  const [selectedIds, setSelectedIds] = useState([])
  const [allSelectedGlobal, setAllSelectedGlobal] = useState(false)
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false)
  const [syncTerminalOpen, setSyncTerminalOpen] = useState(false)

  const params = {
    search,
    page,
    status: filters.status || undefined,
    department: filters.department || undefined,
    position: filters.position || undefined,
  }

  const { data, isLoading } = useQuery({
    queryKey: ['employees', params],
    queryFn: () => employeeService.list(params).then((r) => r.data),
  })

  // Helper selectors
  const pageIds = (data?.results || []).map(e => e.id)
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id))

  const { data: depts } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentService.list({ department_type: 'staff', page_size: 200 }).then((r) => r.data.results || r.data),
  })

  const { data: positions } = useQuery({
    queryKey: ['positions', filters.department],
    queryFn: () => departmentService.positions({
      department: filters.department || undefined
    }).then((r) => r.data.results || r.data),
    enabled: true,
  })

  const toggleSelect = (id) => {
    setAllSelectedGlobal(false)
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    )
  }

  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)))
      setAllSelectedGlobal(false)
    } else {
      setSelectedIds(prev => [...new Set([...prev, ...pageIds])])
    }
  }

  const handleBulkDelete = async () => {
    setDeleting(true)
    try {
      const payload = allSelectedGlobal ? { all_selected: true } : { ids: selectedIds }
      const { page: _, ...filterParams } = params

      await employeeService.bulkDelete(payload, filterParams)
      toast.success(allSelectedGlobal ? `Все записи (${data.count}) удалены` : `Удалено ${selectedIds.length} записей`)
      qc.invalidateQueries(['employees'])
      setSelectedIds([])
      setAllSelectedGlobal(false)
      setBulkDeleteModal(false)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка удаления')
    } finally {
      setDeleting(false)
    }
  }

  const handleImport = async (file) => {
    const resp = await employeeService.importExcel(file)
    qc.invalidateQueries(['employees'])
    return resp
  }

  const handleBulkImport = async (excel, zip) => {
    const resp = await employeeService.bulkImport(excel, zip)
    qc.invalidateQueries(['employees'])
    return resp
  }

  const handleExport = async () => {
    try {
      const resp = await employeeService.exportExcel(params)
      const url = URL.createObjectURL(new Blob([resp.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = 'employees.xlsx'
      a.click()
    } catch {
      toast.error('Ошибка экспорта')
    }
  }

  const handleDownloadTemplate = async () => {
    try {
      const resp = await employeeService.downloadTemplate()
      const url = URL.createObjectURL(new Blob([resp.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = 'employees_template.xlsx'
      a.click()
    } catch {
      toast.error('Ошибка скачивания шаблона')
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await employeeService.delete(deleteModal.id)
      toast.success('Сотрудник удалён')
      qc.invalidateQueries(['employees'])
      setDeleteModal({ open: false, id: null, name: '' })
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка удаления')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Сотрудники</h1>
          {!isLoading && data && (
            <p className="text-sm text-gray-400 mt-0.5">{data.count} записей</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {selectedIds.length > 0 ? (
            <>
              <button className="btn-secondary" onClick={() => setSyncTerminalOpen(true)}>
                <MonitorUp size={16} /> <span className="hidden sm:inline">На терминал</span> ({allSelectedGlobal ? data?.count : selectedIds.length})
              </button>
              <button className="btn-danger" onClick={() => setBulkDeleteModal(true)}>
                <Trash2 size={16} /> <span className="hidden sm:inline">Удалить</span> ({allSelectedGlobal ? data?.count : selectedIds.length})
              </button>
            </>
          ) : (
            <>
              <button className="btn-secondary" onClick={() => setSyncTerminalOpen(true)}>
                <MonitorUp size={16} /> <span className="hidden sm:inline">На терминал</span>
              </button>
              <button className="btn-secondary hidden sm:flex" onClick={() => setBulkImportOpen(true)}>
                <Layers size={16} /> Массовый импорт
              </button>
              <button className="btn-secondary" onClick={() => setImportModalOpen(true)}>
                <Upload size={16} /> <span className="hidden sm:inline">Импорт Excel</span>
              </button>
              <button className="btn-secondary" onClick={handleExport}>
                <Download size={16} /> <span className="hidden sm:inline">Экспорт</span>
              </button>
              <button className="btn-secondary hidden sm:flex" onClick={handleDownloadTemplate}>
                <FileSpreadsheet size={16} /> <span className="hidden sm:inline">Шаблон</span>
              </button>
              <button className="btn-primary" onClick={() => setModal({ open: true, data: null })}>
                <UserPlus size={16} /> <span className="hidden sm:inline">Добавить</span>
              </button>
            </>
          )}
        </div>
      </div>

      <div className="card !p-3">
        {/* Horizontal scrollable filters for mobile to save space */}
        <div className="flex md:flex-wrap overflow-x-auto pb-1 md:pb-0 gap-3 hide-scrollbar">
          <div className="flex-none w-64 md:flex-1 md:w-auto md:min-w-48 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 p-0.5" />
            <input
              className="input pl-9 w-full"
              placeholder="Поиск по ФИО, ID..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            />
          </div>
          <select
            className="input w-auto flex-none"
            value={filters.status}
            onChange={(e) => { setFilters((f) => ({ ...f, status: e.target.value })); setPage(1) }}
          >
            <option value="">Все статусы</option>
            <option value="active">Активен</option>
            <option value="on_leave">В отпуске</option>
            <option value="fired">Уволен</option>
          </select>
          <select
            className="input w-auto flex-none"
            value={filters.department}
            onChange={(e) => { setFilters((f) => ({ ...f, department: e.target.value, position: '' })); setPage(1) }}
          >
            <option value="">Все отделы</option>
            {(depts || []).map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <select
            className="input w-auto flex-none"
            value={filters.position}
            onChange={(e) => { setFilters((f) => ({ ...f, position: e.target.value })); setPage(1) }}
          >
            <option value="">Все должности</option>
            {(positions || []).map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      </div>

      {selectedIds.length > 0 && data?.count > (data?.results || []).length && (
        <div className="bg-primary-50 border border-primary-100 rounded-lg py-2 px-4 flex items-center justify-center gap-2 text-sm text-primary-900">
          {allSelectedGlobal ? (
            <>
              Выбраны все сотрудники ({data.count}).{' '}
              <button
                className="text-primary-600 font-semibold hover:underline"
                onClick={() => {
                  setAllSelectedGlobal(false)
                  setSelectedIds([])
                }}
              >
                Очистить выбор
              </button>
            </>
          ) : (
            <>
              Выбрано <strong>{selectedIds.length}</strong> сотрудников на этой странице.{' '}
              <button
                className="text-primary-600 font-semibold hover:underline"
                onClick={() => setAllSelectedGlobal(true)}
              >
                Выбрать всех ({data.count})
              </button>
            </>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Mobile card list */}
        <div className="md:hidden divide-y divide-gray-100">
          {isLoading ? (
            Array(5).fill(0).map((_, i) => (
              <div key={i} className="p-4 animate-pulse flex gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-100 rounded w-3/4" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                </div>
              </div>
            ))
          ) : (data?.results || []).length === 0 ? (
            <div className="py-12 text-center text-gray-400">
              <UserPlus size={32} className="mx-auto mb-2 opacity-30" />
              <p>Сотрудников не найдено</p>
            </div>
          ) : (
            (data?.results || []).map((emp) => (
              <div
                key={emp.id}
                className={clsx(
                  'relative p-4 bg-white active:bg-gray-50 transition-colors',
                  selectedIds.includes(emp.id) && 'bg-primary-50/50'
                )}
                onClick={() => navigate(`/employees/${emp.id}`)}
              >
                <div className="flex items-center gap-4">
                  {/* Select Checkbox */}
                  <div onClick={(e) => e.stopPropagation()} className="shrink-0">
                    <input type="checkbox" className="checkbox w-5 h-5 rounded-full" checked={selectedIds.includes(emp.id)} onChange={() => toggleSelect(emp.id)} />
                  </div>
                  
                  {/* Avatar */}
                  {emp.photo ? (
                    <img src={emp.photo} className="w-12 h-12 rounded-full object-cover shadow-sm shrink-0" alt="" />
                  ) : (
                    <div className="w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-200 rounded-full flex items-center justify-center text-lg text-gray-500 font-semibold shadow-sm shrink-0">
                      {emp.full_name[0]}
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate text-[15px]">{emp.full_name}</p>
                    <div className="flex flex-col mt-0.5 text-xs text-gray-500 leading-tight">
                      <span className="truncate">{emp.position_name}</span>
                      <span className="truncate text-gray-400">{emp.department_name} · {emp.employee_id}</span>
                    </div>
                  </div>

                  {/* Status & Actions */}
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className={clsx('text-[10px] px-2 py-0.5 rounded-full font-medium', STATUS_BADGE[emp.status] || 'bg-gray-100 text-gray-600')}>
                      {STATUS_LABEL[emp.status] || emp.status}
                    </span>
                    <div className="flex items-center gap-1">
                      <button className="p-1.5 text-gray-400 hover:text-blue-600 active:bg-blue-50 rounded-full transition-colors" onClick={(e) => { e.stopPropagation(); setModal({ open: true, data: emp }) }}><Edit size={16} /></button>
                      <button className="p-1.5 text-gray-400 hover:text-red-600 active:bg-red-50 rounded-full transition-colors" onClick={(e) => { e.stopPropagation(); setDeleteModal({ open: true, id: emp.id, name: emp.full_name }) }}><Trash2 size={16} /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th className="w-10">
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={allSelectedGlobal || allPageSelected}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>#</th>
                <th>ФИО</th>
                <th>ID</th>
                <th>Отдел</th>
                <th>Должность</th>
                <th>График</th>
                <th>На устр.</th>
                <th>Статус</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array(6).fill(0).map((_, i) => (
                  <tr key={i}>
                    {Array(10).fill(0).map((_, j) => (
                      <td key={j}>
                        <div className="h-4 bg-gray-100 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : (data?.results || []).length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-12 text-gray-400">
                    <UserPlus size={32} className="mx-auto mb-2 opacity-30" />
                    <p>Сотрудников не найдено</p>
                  </td>
                </tr>
              ) : (
                (data?.results || []).map((emp, i) => (
                  <tr
                    key={emp.id}
                    className={clsx(
                      "cursor-pointer transition-colors",
                      selectedIds.includes(emp.id) ? "bg-primary-50 hover:bg-primary-100" : "hover:bg-gray-50"
                    )}
                    onClick={() => navigate(`/employees/${emp.id}`)}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="checkbox"
                        checked={selectedIds.includes(emp.id)}
                        onChange={() => toggleSelect(emp.id)}
                      />
                    </td>
                    <td className="text-gray-400 text-xs">{(page - 1) * 20 + i + 1}</td>
                    <td>
                      <div className="flex items-center gap-3">
                        {emp.photo ? (
                          <img src={emp.photo} className="w-8 h-8 rounded-full object-cover" alt="" />
                        ) : (
                          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-xs text-gray-500 font-bold">
                            {emp.full_name[0]}
                          </div>
                        )}
                        <span className="font-medium">{emp.full_name}</span>
                      </div>
                    </td>
                    <td className="font-mono text-xs">{emp.employee_id}</td>
                    <td className="text-gray-500 text-sm">{emp.department_name}</td>
                    <td>{emp.position_name}</td>
                    <td>
                      <span className="badge-gray">
                        {SCHEDULE_LABEL[emp.schedule_type] || emp.schedule_type}
                      </span>
                    </td>
                    <td>
                      {emp.is_registered_on_device
                        ? <span className="badge-green">Да</span>
                        : <span className="badge-gray">Нет</span>}
                    </td>
                    <td>
                      <span className={STATUS_BADGE[emp.status] || 'badge-gray'}>
                        {STATUS_LABEL[emp.status] || emp.status}
                      </span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1">
                        <button
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          onClick={() => setModal({ open: true, data: emp })}
                          title="Редактировать"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          onClick={() => setDeleteModal({ open: true, id: emp.id, name: emp.full_name })}
                          title="Удалить"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data?.count > 20 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-sm text-gray-500">
              Показано {(page - 1) * 20 + 1}–{Math.min(page * 20, data.count)} из {data.count}
            </p>
            <div className="flex gap-1">
              <button
                className="btn-secondary btn-sm"
                disabled={!data.previous}
                onClick={() => setPage((p) => p - 1)}
              >
                Назад
              </button>
              <button
                className="btn-secondary btn-sm"
                disabled={!data.next}
                onClick={() => setPage((p) => p + 1)}
              >
                Вперед
              </button>
            </div>
          </div>
        )}
      </div>

      <EmployeeModal
        open={modal.open}
        onClose={() => setModal({ open: false, data: null })}
        employee={modal.data}
      />

      <ImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        title="Импорт сотрудников"
        onImport={handleImport}
        onDownloadTemplate={handleDownloadTemplate}
      />

      <BulkImportModal
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        title="Массовый импорт сотрудников"
        onImport={handleBulkImport}
        onDownloadTemplate={handleDownloadTemplate}
      />

      <SyncToTerminalModal
        open={syncTerminalOpen}
        onClose={() => {
          setSyncTerminalOpen(false)
          setSelectedIds([])
          setAllSelectedGlobal(false)
        }}
        personType="employee"
        personIds={allSelectedGlobal ? [] : selectedIds}
        allSelected={allSelectedGlobal}
      />

      {/* Bulk Delete Confirm */}
      <Modal
        open={bulkDeleteModal}
        onClose={() => !deleting && setBulkDeleteModal(false)}
        title="Массовое удаление"
        size="sm"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => setBulkDeleteModal(false)}
              disabled={deleting}
            >
              Отмена
            </button>
            <button className="btn-danger" onClick={handleBulkDelete} disabled={deleting}>
              {deleting ? 'Удаление...' : 'Удалить выбранные'}
            </button>
          </>
        }
      >
        <p className="text-gray-600">
          Вы уверены, что хотите удалить <strong>{allSelectedGlobal ? data?.count : selectedIds.length}</strong> выбраных сотрудников?
        </p>
        <p className="text-sm text-red-500 mt-2 font-medium">
          {allSelectedGlobal ? 'ВНИМАНИЕ: Это удалит ВСЕ записи, подходящие под текущие фильтры!' : 'Это действие необратимо!'}
        </p>
      </Modal>

      {/* Delete Confirm */}
      <Modal
        open={deleteModal.open}
        onClose={() => !deleting && setDeleteModal({ open: false, id: null, name: '' })}
        title="Удаление сотрудника"
        size="sm"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => setDeleteModal({ open: false, id: null, name: '' })}
              disabled={deleting}
            >
              Отмена
            </button>
            <button className="btn-danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Удаление...' : 'Удалить'}
            </button>
          </>
        }
      >
        <p className="text-gray-600">
          Удалить сотрудника <strong>«{deleteModal.name}»</strong>?
        </p>
        <p className="text-sm text-gray-400 mt-2">
          История посещаемости сохранится.
        </p>
      </Modal>
    </div>
  )
}
